import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { Plus, Edit2, Trash2, RotateCcw } from 'lucide-react';
import { apiRequest } from '../../utils/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function CountriesPage() {
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCountry, setEditingCountry] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    flag: '',
    currency: '',
    phone_code: '',
    conversion_rate_to_dollar: 1.0,
    is_active: true
  });

  useEffect(() => {
    fetchCountries();
  }, []);

  const fetchCountries = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await apiRequest(`${BACKEND_URL}/api/admin/countries`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setCountries(response.data.countries || []);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching countries:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('admin_token');

    try {
      if (editingCountry) {
        // Update
        await apiRequest(`${BACKEND_URL}/api/admin/countries/${editingCountry.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        });
      } else {
        // Create
        await apiRequest(`${BACKEND_URL}/api/admin/countries`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        });
      }

      setShowModal(false);
      setEditingCountry(null);
      setFormData({ name: '', code: '', flag: '', currency: '', phone_code: '', conversion_rate_to_dollar: 1.0, is_active: true });
      fetchCountries();
    } catch (error) {
      console.error('Error saving country:', error);
      alert('Failed to save country');
    }
  };

  const handleEdit = (country) => {
    setEditingCountry(country);
    setFormData({
      name: country.name,
      code: country.code,
      flag: country.flag,
      currency: country.currency,
      phone_code: country.phone_code || '',
      conversion_rate_to_dollar: country.conversion_rate_to_dollar || 1.0,
      is_active: country.is_active
    });
    setShowModal(true);
  };

  const handleDeactivate = async (countryId) => {
    if (!window.confirm('Are you sure you want to deactivate this country? It will be moved to the inactive list.')) return;

    const token = localStorage.getItem('admin_token');
    try {
      await apiRequest(`${BACKEND_URL}/api/admin/countries/${countryId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      fetchCountries();
    } catch (error) {
      console.error('Error deactivating country:', error);
      alert('Failed to deactivate country');
    }
  };

  const handleRestore = async (countryId) => {
    const token = localStorage.getItem('admin_token');
    try {
      await apiRequest(`${BACKEND_URL}/api/admin/countries/${countryId}/restore`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      fetchCountries();
    } catch (error) {
      console.error('Error restoring country:', error);
      alert('Failed to restore country');
    }
  };

  // Separate active and inactive countries
  const activeCountries = countries.filter(c => c.is_active !== false);
  const inactiveCountries = countries.filter(c => c.is_active === false);

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy mx-auto"></div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  const CountryTable = ({ countryList, isInactive = false }) => (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
      <table className="w-full">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Flag</th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Name</th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Code</th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Currency</th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Phone Code</th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Rate to USD</th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
            <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {countryList.map((country) => (
            <tr key={country.id} className={`hover:bg-gray-50 ${isInactive ? 'bg-gray-50/50' : ''}`}>
              <td className="px-6 py-4">
                <span className="text-3xl">{country.flag}</span>
              </td>
              <td className="px-6 py-4">
                <span className={`font-semibold ${isInactive ? 'text-gray-500' : 'text-navy'}`}>{country.name}</span>
              </td>
              <td className="px-6 py-4">
                <span className="text-gray-600 font-mono text-sm">{country.code}</span>
              </td>
              <td className="px-6 py-4">
                <span className="text-gray-600">{country.currency}</span>
              </td>
              <td className="px-6 py-4">
                <span className="text-gray-600 font-mono">{country.phone_code || '—'}</span>
              </td>
              <td className="px-6 py-4">
                <span className="text-gray-600 font-mono">{country.conversion_rate_to_dollar || 1.0}</span>
              </td>
              <td className="px-6 py-4">
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  isInactive
                    ? 'bg-red-100 text-red-800'
                    : 'bg-green-100 text-green-800'
                }`}>
                  {isInactive ? 'Inactive' : 'Active'}
                </span>
              </td>
              <td className="px-6 py-4">
                <div className="flex items-center justify-end gap-2">
                  {isInactive ? (
                    <button
                      onClick={() => handleRestore(country.id)}
                      className="p-2 text-green-600 hover:bg-green-50 rounded-lg flex items-center gap-1"
                      title="Restore Country"
                    >
                      <RotateCcw className="w-4 h-4" />
                      <span className="text-sm">Restore</span>
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={() => handleEdit(country)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeactivate(country.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                        title="Deactivate"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {countryList.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">
            {isInactive ? 'No inactive countries' : 'No countries found. Add your first country!'}
          </p>
        </div>
      )}
    </div>
  );

  return (
    <AdminLayout>
      <div className="p-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-extrabold text-navy">Countries</h1>
            <p className="text-gray-600 mt-1">Manage countries for signup</p>
          </div>
          <button
            onClick={() => {
              setEditingCountry(null);
              setFormData({ name: '', code: '', flag: '', currency: '', phone_code: '', conversion_rate_to_dollar: 1.0, is_active: true });
              setShowModal(true);
            }}
            className="bg-navy text-white px-6 py-3 rounded-lg hover:bg-navy/90 flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Add Country
          </button>
        </div>

        {/* Active Countries Section */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-navy mb-4 flex items-center gap-2">
            <span className="w-3 h-3 bg-green-500 rounded-full"></span>
            Active Countries ({activeCountries.length})
          </h2>
          <CountryTable countryList={activeCountries} isInactive={false} />
        </div>

        {/* Inactive Countries Section */}
        {inactiveCountries.length > 0 && (
          <div>
            <h2 className="text-xl font-bold text-gray-600 mb-4 flex items-center gap-2">
              <span className="w-3 h-3 bg-red-500 rounded-full"></span>
              Inactive Countries ({inactiveCountries.length})
            </h2>
            <CountryTable countryList={inactiveCountries} isInactive={true} />
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-md w-full p-6">
              <h2 className="text-2xl font-bold text-navy mb-6">
                {editingCountry ? 'Edit Country' : 'Add Country'}
              </h2>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Country Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Country Code (ISO) *
                  </label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-transparent font-mono"
                    placeholder="US, IN, GB"
                    maxLength={3}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Flag (Emoji) *
                  </label>
                  <input
                    type="text"
                    value={formData.flag}
                    onChange={(e) => setFormData({ ...formData, flag: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-transparent text-3xl"
                    placeholder="🇺🇸"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Enter country flag emoji
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Currency *
                  </label>
                  <input
                    type="text"
                    value={formData.currency}
                    onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-transparent"
                    placeholder="USD, INR, GBP"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Country Phone Code
                  </label>
                  <input
                    type="text"
                    value={formData.phone_code}
                    onChange={(e) => setFormData({ ...formData, phone_code: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-transparent font-mono"
                    placeholder="+91, +1, +44"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    International dialing code shown next to phone numbers in the users listing (e.g., +91, +1, +44)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Conversion Rate to Dollar *
                  </label>
                  <input
                    type="number"
                    step="0.0001"
                    min="0"
                    value={formData.conversion_rate_to_dollar}
                    onChange={(e) => setFormData({ ...formData, conversion_rate_to_dollar: parseFloat(e.target.value) || 1.0 })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-transparent font-mono"
                    placeholder="1.0"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    How many units of this currency equals 1 USD (e.g., INR: 83.5, EUR: 0.92)
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-5 h-5 text-navy focus:ring-navy rounded"
                  />
                  <label htmlFor="is_active" className="text-sm font-semibold text-gray-700">
                    Active (Show in signup)
                  </label>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingCountry(null);
                      setFormData({ name: '', code: '', flag: '', currency: '', phone_code: '', conversion_rate_to_dollar: 1.0, is_active: true });
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-navy text-white rounded-lg hover:bg-navy/90"
                  >
                    {editingCountry ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
